import type { IMcpSessionData } from '@beefree.io/sdk/dist/types/bee';
import { createGlobalState } from '@vueuse/core';
import { ref } from 'vue';

type McpSessionStarter = () => Promise<IMcpSessionData>;

/**
 * Shared state linking the chatbot to the live Bee SDK editor.
 *
 * Beefree's v2 MCP server routes tool calls to the editor by the
 * `templateId` that `bee.startMcpSession()` returns; the backend sends it
 * as the `x-bee-template-id` header. BeeEditor.vue registers its
 * `startMcpSession` here on mount, and useChatBot calls `start()` before
 * each message. An editor-managed session ends after ~10 minutes, when
 * the agent leaves or when a new template loads, so each chat message
 * gets a fresh one, as Beefree recommends.
 *
 * `templateSnapshot` holds a truncated JSON string of the current editor
 * content. The backend injects it into the system prompt so the model can
 * reason about structure without burning a `beefree_get_content_hierarchy`
 * call first.
 *
 * @see https://docs.beefree.io/beefree-sdk/mcp-server/installation-and-setup
 */
export const useBeeMcpSession = createGlobalState(() => {
    const templateId = ref<string | null>(null);
    const templateSnapshot = ref<string | null>(null);

    let starter: McpSessionStarter | null = null;

    function register(startMcpSession: McpSessionStarter): void {
        starter = startMcpSession;
    }

    async function start(): Promise<string | null> {
        templateId.value = null;

        if(!starter) {
            return null;
        }

        try {
            // On failure the SDK may resolve with an error object instead of rejecting.
            const result: Partial<IMcpSessionData> | undefined = await starter();

            templateId.value = typeof result?.templateId === 'string' ? result.templateId : null;
        } catch (error) {
            console.warn('Could not start a Beefree MCP session', error);
        }

        return templateId.value;
    }

    function setSnapshot(value: string): void {
        templateSnapshot.value = value;
    }

    function clear(): void {
        starter = null;
        templateId.value = null;
        templateSnapshot.value = null;
    }

    return { templateId, templateSnapshot, register, start, setSnapshot, clear };
});
